import React from "react";
import PropTypes from "prop-types";

import { withStyles } from "@material-ui/core/styles";
import classNames from "classnames";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import SimpleAnchor from "./../common/SimpleAnchor";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";

// Icons
import ShippingIcon from "@material-ui/icons/LocalShipping";
import BuildIcon from "@material-ui/icons/Build";
import StoreMallDirectoryIcon from "@material-ui/icons/StoreMallDirectory";
import PhoneIcon from "@material-ui/icons/Phone";
import NewIcon from "@material-ui/icons/CallReceived";
import MoneyIcon from "@material-ui/icons/AttachMoney";
import HomeIcon from "@material-ui/icons/Home";
import CheckIcon from "@material-ui/icons/Check";
import CardIcon from "@material-ui/icons/CreditCard";
import EmailIcon from "@material-ui/icons/Email";

import moment from "moment";
import commons from "./../common/commons";
import { isEuropeanStockProduct } from "./../checkout-page/europeanStock";
import { EU_STOCK_FLAG_IMAGE } from "./../common/stockFlagImages";

moment.updateLocale("en", {
  relativeTime: {
    future: "בעוד %s",
    past: "לפני %s",
    s: "כמה שניות",
    ss: "%d שניות",
    m: "דקה",
    mm: "%d דקות",
    h: "שעה",
    hh: "%d שעות",
    d: "יום",
    dd: "%d ימים",
    M: "חודש",
    MM: "%d חודשים",
    y: "שנה",
    yy: "%d שנים",
  },
});

const hebrew = {
  sips: {
    pickup: "איסוף עצמי",
    shipping: "משלוח",
    install: "הרכבה במוסך",
  },
  sipInfos: {
    install: "הלקוח בחר התקנה ב: %s",
    installDate: "תאריך התקנה: %s",
    installSlot: "שעת התקנה: %s",
    manualBooking: "הלקוח יתאם עם המוסך כשהצמיגים יגיעו",
  },
  statuses: {
    open: "הזמנה התקבלה",
    paid: "הזמנה שולמה",
    transit: "בדרך למוסך",
    doneTransit: "הצמיגים במוסך",
    cancelled: "מבוטלת",
    sent: "נשלחה",
    closed: "סגורה",
  },
  callCTA: "חייג",
  orderNum: "מספר הזמנה: %s",
};

function paymentText(value) {
  if (value == null) return "";
  return String(value).trim();
}

function firstPaymentText(...values) {
  for (const value of values) {
    const normalized = paymentText(value);
    if (normalized) return normalized;
  }
  return "";
}

function visibleCardMask(value) {
  const normalized = paymentText(value);
  const digits = normalized.replace(/\D/g, "");
  if (digits.length < 4) return "";
  if (/[*xX]/.test(normalized)) return normalized;
  return `****${digits.slice(-4)}`;
}

function firstCardMask(...values) {
  for (const value of values) {
    const mask = visibleCardMask(value);
    if (mask) return mask;
  }
  return "";
}

const PAYMENT_METHOD_LABELS = {
  creditcard: "כרטיס אשראי",
  creditcard_onphone: "כרטיס אשראי באמצעות הטלפון",
  cash: "מזומן",
  transfer: "העברה בנקאית",
  transfer_bit: "ביט",
};

function normalizePaymentMethod(checkout) {
  const raw = firstPaymentText(
    checkout?.selectedPaymentMethod,
    checkout?.paymentMethod
  )
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (raw === "creditcard" || raw === "credit") return "creditcard";
  if (raw === "creditcardonphone") return "creditcard_onphone";
  if (raw === "banktransfer" || raw === "transfer") return "transfer";
  if (raw === "transferbit" || raw === "bit") return "transfer_bit";
  if (raw === "cash") return "cash";
  return raw;
}

function linesFromPaymentDisplay(display) {
  const lines = [];
  const outcome = display.gatewayOutcome;
  if (outcome && outcome.label) {
    const value = outcome.detail
      ? `${outcome.label} — ${outcome.detail}`
      : outcome.label;
    lines.push(["סטטוס עסקה", value, outcome.state]);
  }
  if (display.methodLabel) lines.push(["אופן התשלום", display.methodLabel]);
  if (display.cardMask) lines.push(["כרטיס", display.cardMask]);
  if (
    display.method === "creditcard" ||
    display.method === "creditcard_onphone" ||
    display.payments
  ) {
    lines.push(["תשלומים", String(display.payments || 1)]);
  }
  if (display.authNumber) lines.push(["מספר אישור", display.authNumber]);
  if (display.txId) lines.push(["מזהה עסקה", display.txId]);
  return lines;
}

// Keep in sync with edge/services/order-payment-display.js
function buildPaymentLines(order) {
  const display = order?.paymentDisplay;
  if (
    display &&
    (display.method ||
      display.cardMask ||
      display.authNumber ||
      display.txId ||
      display.gatewayOutcome)
  ) {
    return linesFromPaymentDisplay(display);
  }

  const checkout = order?.checkout || {};
  const deposit = order?.depositDetails || {};
  const proofs = [
    order?.finalizedData,
    deposit.depositFinalizedData,
    deposit.remainderFinalizedData,
  ].filter(Boolean);
  const method = normalizePaymentMethod(checkout);
  const cardMask = firstCardMask(
    ...proofs.flatMap((proof) => [
      proof.cardMask,
      proof.verification && proof.verification.cardMask,
    ]),
    checkout.payment && checkout.payment.cardNumber
  );
  const authNumber = firstPaymentText(
    ...proofs.flatMap((proof) => [
      proof.authNumber,
      proof.verification && proof.verification.authNumber,
    ])
  );
  const txId = firstPaymentText(
    ...proofs.flatMap((proof) => [
      proof.txId,
      proof.verification && proof.verification.txId,
    ]),
    order?.ccResult?.txId,
    deposit.depositTxId,
    deposit.remainderTxId,
    order?.remainderCcResult?.txId
  );
  const paymentsNumber = Number(checkout.payment && checkout.payment.payments);
  const payments =
    Number.isFinite(paymentsNumber) && paymentsNumber > 0
      ? paymentsNumber
      : null;

  const lines = [];
  const methodLabel = PAYMENT_METHOD_LABELS[method];
  if (methodLabel) lines.push(["אופן התשלום", methodLabel]);
  if (cardMask) lines.push(["כרטיס", cardMask]);
  if (method === "creditcard" || method === "creditcard_onphone" || payments) {
    lines.push(["תשלומים", String(payments || 1)]);
  }
  if (authNumber) lines.push(["מספר אישור", authNumber]);
  if (txId) lines.push(["מזהה עסקה", txId]);
  return lines;
}

const sipChipCommon = {
  fontWeight: 700,
};

const styles = (theme) => ({
  card: {
    width: "100%",
    marginTop: theme.spacing(2),
    position: "relative",
    overflow: "visible",
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1.4fr) minmax(380px, 2fr) minmax(180px, auto)",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
    },
  },
  customerSummary: {
    minWidth: 0,
  },
  customerName: {
    fontWeight: 700,
  },
  orderMeta: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  summaryChips: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  amountBlock: {
    textAlign: "left",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      textAlign: "right",
    },
  },
  amountLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
  },
  amount: {
    fontSize: "1.4rem",
    fontWeight: 800,
    color: "#087f23",
  },
  simulatedChip: {
    backgroundColor: "#ede9fe",
    color: "#5b21b6",
    fontWeight: 700,
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(320px, 1.35fr) minmax(260px, 1fr)",
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  detailsGridWithPayment: {
    gridTemplateColumns:
      "minmax(280px, 1.35fr) minmax(220px, 1fr) minmax(200px, 0.9fr)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  detailsSection: {
    padding: theme.spacing(2),
    minWidth: 0,
  },
  deliverySection: {
    padding: theme.spacing(2),
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      borderRight: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  itemList: {
    padding: 0,
  },
  itemRow: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  itemMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  euFlag: {
    width: 22,
    height: 15,
    objectFit: "cover",
    borderRadius: 2,
  },
  euStockChip: {
    backgroundColor: "#eef2ff",
    color: "#243c8f",
    fontWeight: 700,
  },
  localStockChip: {
    backgroundColor: "#f3f4f6",
    color: "#4b5563",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    "& > *": {
      margin: 0,
    },
  },
  content: {
    padding: 5,
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  sipInfoText: {
    fontSize: 16,
  },
  paymentApproved: {
    color: "#087f23",
    fontWeight: 700,
  },
  paymentDeclined: {
    color: "#c62828",
    fontWeight: 700,
  },
  formControl: {
    //margin: theme.spacing(),
  },
  statusUpdateSelect: {
    width: 140,
  },
  sipPickupChip: Object.assign(
    {
      background: "#e53966",
      color: "#fefefe",
    },
    sipChipCommon
  ),
  sipInstallChip: Object.assign(
    {
      background: "#0079b5",
      color: "#fefefe",
    },
    sipChipCommon
  ),
  sipShippingChip: Object.assign(
    {
      background: "#f4b342",
      color: "#313330",
    },
    sipChipCommon
  ),
  sipShippingAvatar: {
    background: "#e5a639",
    color: "#313330",
  },
  sipInstallAvatar: {
    background: "#0494db",
    color: "#fefefe",
  },
  sipPickupAvatar: {
    background: "#d6355f",
    color: "#fefefe",
  },
  button: {
    margin: theme.spacing(),
  },
  cardHeader: {
    flip: true,
    direction: "ltr",
  },
  chip: {
    margin: 0,
  },
  statusChipOpen: {
    margin: 0,
    background: "#ffe266",
    color: "#35332b",
  },
  orderItemAvatar: {
    width: 60,
    height: 60,
  },
  statusAvatarOpen: {
    background: "#f2cf3c",
    color: "#35332b",
  },
  statusChipPaid: {
    margin: 0,
    background: "#10ba16",
    color: "#121912",
  },
  statusChipTransit: {
    margin: 0,
    background: "#f4b342",
    color: "#313330",
  },
  statusChipSent: {
    margin: 0,
    background: "#f4b342",
    color: "#313330",
  },
  statusChipDoneTransit: {
    margin: 0,
    background: "#36b8e8",
    color: "#313330",
  },
  statusAvatarDoneTransit: {
    background: "#36b8e8",
    color: "#313330",
  },
  statusChipClosed: {
    margin: 0,
    background: "#7c68d6",
    color: "#fefefe",
  },
  statusAvatarClosed: {
    background: "#8f79f2",
    color: "#fefefe",
  },
  statusAvatarTransit: {
    background: "#e8a737",
    color: "#313330",
  },
  statusAvatarSent: {
    background: "#e8a737",
    color: "#313330",
  },
  statusAvatarPaid: {
    background: "#09a30e",
    color: "#121912",
  },
  quantity: {
    fontWeight: 700,
  },
});

class OrderCard extends React.Component {
  state = { expanded: false };

  findProduct = (ean) =>
    this.props.products.find((product) => String(product.ean) === String(ean));

  isEuropeanStockItem = (item) =>
    isEuropeanStockProduct(this.findProduct(item.ean), item);

  getEuropeanStockQuantity = () => {
    const {
      data: { cart = {}, onDemand },
    } = this.props;
    const items = cart.items || [];
    const quantity = items.reduce(
      (total, item) =>
        total +
        (this.isEuropeanStockItem(item) ? Number(item.quantity) || 0 : 0),
      0
    );

    return quantity || (onDemand && onDemand.hasItems ? null : 0);
  };

  getOrderTotal = () => {
    const { data } = this.props;
    // Explicit nulls are common; destructuring defaults only cover undefined
    const cart = data?.cart || {};
    const checkout = data?.checkout || {};
    const ccResult = data?.ccResult || {};
    const appliedDiscount = data?.appliedDiscount || 0;
    const chargedAgorot = Number(ccResult.expectedAmountAgorot);

    if (
      ccResult.expectedAmountAgorot != null &&
      Number.isFinite(chargedAgorot) &&
      chargedAgorot >= 0
    ) {
      return chargedAgorot / 100;
    }

    const storedTotal = Number(cart.total);
    if (
      cart.total != null &&
      Number.isFinite(storedTotal) &&
      storedTotal >= 0
    ) {
      return storedTotal;
    }

    const items = cart.items || [];
    let hasMissingPrice = false;
    const subtotal = items.reduce((total, item) => {
      const product = this.findProduct(item.ean);
      const price = Number(item.price != null ? item.price : product && product.price);
      if (!Number.isFinite(price)) {
        hasMissingPrice = true;
        return total;
      }
      return total + price * (Number(item.quantity) || 0);
    }, 0);

    if (hasMissingPrice) return null;

    const shippingCost = Number(
      cart.shippingCost != null ? cart.shippingCost : checkout.shippingCost
    );
    return Math.max(
      0,
      subtotal +
        (Number.isFinite(shippingCost) ? shippingCost : 0) -
        (Number(appliedDiscount) || 0)
    );
  };

  renderOrderBody() {
    const {
      classes,
      products,
      data: { cart },
    } = this.props;

    return (
      <div className={classes.root}>
        <List className={classes.itemList}>
          {cart.items
            .map((item, index) => {
              const { ean, quantity } = item;
              const product = products.find(
                (candidate) => String(candidate.ean) === String(ean)
              );
              if (!product) {
                return null;
              }
              const { brand, displayName, displaySize, cdn = false } = product;
              const primaryPhoto = commons.getProductPrimaryImage(cdn, ean);
              const isEuropeanStock = isEuropeanStockProduct(product, item);

              return (
                <ListItem className={classes.itemRow} key={ean || index}>
                  <Avatar
                    className={classes.orderItemAvatar}
                    src={primaryPhoto.url}
                  />
                  <ListItemText
                    primary={`${brand} ${displayName}`}
                    secondary={displaySize}
                  />
                  <div className={classes.itemMeta}>
                    {isEuropeanStock && (
                      <img
                        className={classes.euFlag}
                        src={EU_STOCK_FLAG_IMAGE}
                        alt="מלאי אירופי"
                      />
                    )}
                    <Chip
                      className={classes.quantity}
                      label={`${quantity} יח׳`}
                      size="small"
                    />
                  </div>
                </ListItem>
              );
            })
            .filter((elem) => elem !== null)}
        </List>
      </div>
    );
  }

  updateOrderStatus = () => {
    if ("status" in this.state) {
      const {
        data: { orderId },
      } = this.props;
      this.props.onUpdateStatus(orderId, this.state.status);
    }
  };

  saveTrackingCode = () => {
    if ("trackingCode" in this.state) {
      const {
        data: { orderId },
      } = this.props;
      this.props.onUpdateTrackingCode(orderId, this.state.trackingCode);
    }
  };

  handleTrackingChange = (evt) => {
    this.setState({ trackingCode: evt.target.value });
  };

  handleChange = (evt) => {
    this.setState({ status: evt.target.value });
  };

  getPaymentDetails = () => {
    const { token, data } = this.props;
    const { orderId } = data;
    this.props.onGetPaymentDetails({ orderId, token });
  };

  resendPurchaseEmails = () => {
    const {
      data: { orderId },
    } = this.props;
    if (this.props.onResendPurchaseEmails) {
      this.props.onResendPurchaseEmails(orderId);
    }
  };

  render() {
    const {
      classes,
      during,
      data: {
        checkout,
        createdAt,
        isPaymentSimulation = false,
        trackingCode = "",
        status,
        orderId,
        cData = null,
      },
    } = this.props;

    const {
      personal = {},
      sip,
      shop = {},
      installDate,
      installSlot,
      shipping = {},
    } = checkout;

    const { name = "", phone = "" } = personal;

    const momInstance = moment(createdAt);
    let sipChipClassname,
      sipChipAvatarClassname,
      sipChipIcon,
      sipInformation,
      sipChipLabel;

    switch (sip) {
      case "install":
        sipChipClassname = "sipInstallChip";
        sipChipAvatarClassname = "sipInstallAvatar";
        sipChipLabel = hebrew.sips.install;
        sipChipIcon = <BuildIcon />;
        sipInformation = (
          <div>
            <Typography className={classes.sipInfoText}>
              {hebrew.sipInfos.install.replace("%s", shop.displayName)} <br />
              {"manualBooking" in shop && hebrew.sipInfos.manualBooking}
              {!shop.manualBooking &&
                hebrew.sipInfos.installDate.replace(
                  "%s",
                  moment(installDate).format("DD/MM/YYYY")
                )}{" "}
              <br />
              {!shop.manualBooking &&
                hebrew.sipInfos.installSlot.replace(
                  "%s",
                  `${installSlot + 2}:00 - ${installSlot}:00`
                )}
            </Typography>
          </div>
        );
        break;

      case "pickup":
        sipChipClassname = "sipPickupChip";
        sipChipAvatarClassname = "sipPickupAvatar";
        sipChipLabel = hebrew.sips.pickup;
        sipChipIcon = <StoreMallDirectoryIcon />;
        break;

      case "shipping":
        sipChipClassname = "sipShippingChip";
        sipChipAvatarClassname = "sipShippingAvatar";
        sipChipLabel = hebrew.sips.shipping;
        sipChipIcon = <ShippingIcon />;
        sipInformation = (
          <div>
            <Typography className={classes.sipInfoText}>
              {shipping.name || name} <br />
              {shipping.address} <br />
              {shipping.city} <br />
              <SimpleAnchor to={`tel: ${shipping.phone || phone}`}>
                {shipping.phone || phone}
              </SimpleAnchor>{" "}
              <br />
              {shipping.notes !== undefined && shipping.notes}
            </Typography>
          </div>
        );
        break;
    }

    let statusChipClassname, statusAvatarClassname, statusChipIcon;

    switch (status) {
      case "open":
        statusChipClassname = "statusChipOpen";
        statusAvatarClassname = "statusAvatarOpen";
        statusChipIcon = <NewIcon />;
        break;

      case "paid":
        statusChipClassname = "statusChipPaid";
        statusAvatarClassname = "statusAvatarPaid";
        statusChipIcon = <MoneyIcon />;
        break;

      case "transit":
        statusChipClassname = "statusChipTransit";
        statusAvatarClassname = "statusAvatarTransit";
        statusChipIcon = <ShippingIcon />;
        break;

      case "sent":
        statusChipClassname = "statusChipSent";
        statusAvatarClassname = "statusAvatarSent";
        statusChipIcon = <ShippingIcon />;
        break;

      case "doneTransit":
        statusChipClassname = "statusChipDoneTransit";
        statusAvatarClassname = "statusAvatarDoneTransit";
        statusChipIcon = <HomeIcon />;
        break;

      case "closed":
        statusChipClassname = "statusChipClosed";
        statusAvatarClassname = "statusAvatarClosed";
        statusChipIcon = <CheckIcon />;
        break;
    }

    const orderTotal = this.getOrderTotal();
    const europeanStockQuantity = this.getEuropeanStockQuantity();
    const hasEuropeanStock = europeanStockQuantity !== 0;
    const paymentLines = buildPaymentLines(this.props.data);
    const showStoredCard =
      cData &&
      cData.num &&
      cData.num !== paymentLines.find(([label]) => label === "כרטיס")?.[1];

    return (
      <Card className={classes.card}>
        <div className={classes.summary}>
          <div className={classes.customerSummary}>
            <Typography variant="h6" className={classes.customerName}>
              {name}
            </Typography>
            <Typography variant="body2" className={classes.orderMeta}>
              {momInstance.format("HH:mm DD/MM/YYYY")} ({momInstance.fromNow()})
            </Typography>
          </div>

          <div className={classes.summaryChips}>
            {isPaymentSimulation && (
              <Chip
                className={classes.simulatedChip}
                label="Simulated"
                size="small"
              />
            )}
            <Chip
              className={classes.chip}
              label={hebrew.orderNum.replace("%s", orderId)}
            />
            <Chip
              className={classes[statusChipClassname]}
              avatar={
                <Avatar className={classes[statusAvatarClassname]}>
                  {statusChipIcon}
                </Avatar>
              }
              label={hebrew.statuses[status] || status}
            />
            <Chip
              className={classes[sipChipClassname]}
              avatar={
                <Avatar className={classes[sipChipAvatarClassname]}>
                  {sipChipIcon}
                </Avatar>
              }
              label={sipChipLabel}
            />
            <Chip
              className={
                hasEuropeanStock
                  ? classes.euStockChip
                  : classes.localStockChip
              }
              avatar={
                hasEuropeanStock ? (
                  <Avatar src={EU_STOCK_FLAG_IMAGE} alt="דגל האיחוד האירופי" />
                ) : undefined
              }
              label={
                hasEuropeanStock
                  ? `מלאי אירופי${
                      europeanStockQuantity
                        ? ` · ${europeanStockQuantity} צמיגים`
                        : ""
                    }`
                  : "מלאי מקומי"
              }
            />
          </div>

          <div className={classes.amountBlock}>
            <Typography className={classes.amountLabel}>סה״כ ששולם</Typography>
            <Typography className={classes.amount}>
              {orderTotal == null
                ? "לא זמין"
                : `₪${orderTotal.toLocaleString("he-IL", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}`}
            </Typography>
          </div>
        </div>
        <Divider />
        <div
          className={classNames(classes.detailsGrid, {
            [classes.detailsGridWithPayment]:
              paymentLines.length > 0 || showStoredCard,
          })}
        >
          <CardContent className={classes.detailsSection}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              פריטי ההזמנה
            </Typography>
            {this.renderOrderBody()}
          </CardContent>
          <CardContent className={classes.deliverySection}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              פרטי אספקה
            </Typography>
            {sipInformation || (
              <Typography className={classes.sipInfoText}>
                <SimpleAnchor to={`tel:${phone}`}>{phone}</SimpleAnchor>
              </Typography>
            )}
          </CardContent>
          {(paymentLines.length > 0 || showStoredCard) && (
            <CardContent className={classes.deliverySection}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                פרטי תשלום
              </Typography>
              {paymentLines.map(([label, value, tone]) => (
                <Typography
                  key={label}
                  className={classNames(classes.sipInfoText, {
                    [classes.paymentApproved]: tone === "approved",
                    [classes.paymentDeclined]:
                      tone === "declined" || tone === "failed",
                  })}
                >
                  {label}: {value}
                </Typography>
              ))}
              {showStoredCard && (
                <Typography className={classes.sipInfoText}>
                  {cData.noc}
                  <br />
                  {cData.num}
                  <br />
                  {cData.ex}
                  <br />
                  {cData.vc}
                  <br />
                  תשלומים: {cData.ins}
                </Typography>
              )}
            </CardContent>
          )}
        </div>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            variant="contained"
            to={`tel:${phone}`}
            component={SimpleAnchor}
          >
            <PhoneIcon />
          </Button>

          <Button
            variant="contained"
            disabled={during}
            onClick={this.resendPurchaseEmails}
            title="שליחת אימייל אישור הזמנה מחדש (ללקוח ולמנהלים)"
          >
            <EmailIcon />
          </Button>

          <FormControl className={classes.formControl}>
            <InputLabel>עדכון הזמנה</InputLabel>
            <Select
              disabled={during}
              className={classes.statusUpdateSelect}
              value={this.state.status || ""}
              onChange={this.handleChange}
            >
              <MenuItem value={"open"}>{hebrew.statuses.open}</MenuItem>
              <MenuItem value={"paid"}>{hebrew.statuses.paid}</MenuItem>
              {sip === "install" && (
                <MenuItem value={"transit"}>{hebrew.statuses.transit}</MenuItem>
              )}
              {sip === "install" && (
                <MenuItem value={"doneTransit"}>
                  {hebrew.statuses.doneTransit}
                </MenuItem>
              )}
              {sip === "shipping" && (
                <MenuItem value={"sent"}>{hebrew.statuses.sent}</MenuItem>
              )}
              <MenuItem value={"closed"}>{hebrew.statuses.closed}</MenuItem>
              <MenuItem value={"cancelled"}>
                {hebrew.statuses.cancelled}
              </MenuItem>
            </Select>
          </FormControl>

          <Button variant="contained" onClick={this.updateOrderStatus}>
            עדכון
          </Button>

          <Button variant="contained" onClick={this.getPaymentDetails}>
            <CardIcon />
          </Button>
        </CardActions>
        <CardActions className={classes.actions}>
          <TextField
            label="קוד מעקב"
            disabled={during}
            value={this.state.trackingCode || trackingCode}
            onChange={this.handleTrackingChange}
            margin="normal"
          />
          <Button variant="contained" onClick={this.saveTrackingCode}>
            שמור
          </Button>
        </CardActions>
      </Card>
    );
  }
}

OrderCard.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  products: PropTypes.array.isRequired,
  token: PropTypes.string,
  during: PropTypes.bool,
  onGetPaymentDetails: PropTypes.func,
  onUpdateStatus: PropTypes.func,
  onUpdateTrackingCode: PropTypes.func,
  onResendPurchaseEmails: PropTypes.func,
};

export default withStyles(styles)(OrderCard);
